/*
 * assets/sx-fit-assurance.js
 * Struxa Fit Assurance - match confidence, badges, and checkout plumbing.
 *
 * ONE TIER ONLY since 14/09/2026. Fit Assurance covers an exact OEM match
 * and nothing else. The same wording lives in assets/sx-fit-assurance.js,
 * snippets/sx-fit-assurance.liquid, snippets/sx-fit-cart-summary.liquid and
 * templates/page.faq.json - all four must say the same thing.
 *
 * Two tiers, and the customer never sees a percentage:
 *   1  Guaranteed fitment   - an exact OEM match from the rego lookup or scanner
 *   2  Check force rating   - length/fittings match, but the force is unknown
 *
 * Nothing in here decides tiers on its own. The finder, rego lookup and scanner
 * call sxFit.fromMatch(...) when they know what they matched; this file stores
 * that, decides what to show on a product, and attaches it to the cart so it
 * survives through to checkout, the order, and the emails.
 *
 * WIRING - one line per outcome:
 *
 *   // rego lookup found an exact OEM match
 *   sxFit.fromMatch({ exact: true, source: 'rego',
 *                     vehicle: '2011 Ford Focus II', plate: plate,
 *                     skus: matches.map(function (m) { return m.sku; }) });
 *
 *   // rego matched the vehicle but not the pressure
 *   sxFit.fromMatch({ exact: false, source: 'rego', vehicle: label, plate: plate });
 *
 *   // scanner read the label and matched it exactly
 *   sxFit.fromMatch({ exact: true, source: 'scanner', code: code, skus: [sku] });
 *
 *   // customer browsed to it themselves
 *   sxFit.fromMatch({ exact: false, source: 'finder', vehicle: make + ' ' + model });
 */
(function () {
  'use strict';

  var KEY = 'sx_fit_v1';
  var TTL_MS = 1000 * 60 * 60 * 6;      // a match goes stale after 6 hours

  // ---------------------------------------------------------------- storage
  function read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.at || Date.now() - v.at > TTL_MS) { clear(); return null; }
      return v;
    } catch (e) { return null; }
  }

  function write(v) {
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
  }

  function clear() {
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }

  // ----------------------------------------------------------------- public
  var sxFit = {
    /**
     * Record what the customer just matched. Low-level; prefer fromMatch().
     *
     * @param {Object} m
     * @param {number} m.tier      1 = exact OEM match, 2 = partial match
     * @param {string} m.source    'rego' | 'scanner' | 'finder' | 'manual'
     * @param {string} [m.vehicle] "2014 Toyota Corolla Hatchback"
     * @param {string} [m.plate]   registration, if that is how they got here
     * @param {string} [m.code]    the part number read off the old strut
     * @param {string[]} [m.skus]  the SKUs this match applies to. Tier 1 only ever
     *                             applies to these; anything else falls back to tier 2.
     */
    set: function (m) {
      if (!m || (m.tier !== 1 && m.tier !== 2)) return;
      write({
        tier: m.tier,
        source: m.source || 'finder',
        vehicle: m.vehicle || '',
        plate: m.plate || '',
        code: m.code || '',
        skus: Array.isArray(m.skus) ? m.skus.slice(0, 60) : [],
        at: Date.now()
      });
      document.dispatchEvent(new CustomEvent('sx:fit-changed'));
      if (window.sxTrack) {
        window.sxTrack('fit_assurance_matched', {
          fit_tier: m.tier === 1 ? 'guaranteed' : 'partial',
          source: m.source || 'finder'
        });
      }
    },

    /**
     * The call the finder, rego lookup and scanner should make.
     * Give it what the lookup found; it works out the tier.
     *
     * @param {Object} r
     * @param {boolean} r.exact    true only for a confirmed exact OEM match
     * @param {string}  r.source   'rego' | 'scanner' | 'finder'
     * @param {string} [r.vehicle] plain-language vehicle, shown to the customer
     * @param {string} [r.plate]
     * @param {string} [r.code]
     * @param {string[]} [r.skus]  REQUIRED for an exact match
     */
    fromMatch: function (r) {
      if (!r) return;
      var skus = Array.isArray(r.skus) ? r.skus.filter(Boolean) : [];
      // An exact match that does not name a SKU is a promise we could not keep,
      // so it is recorded as partial. This is the one rule worth being strict about.
      var tier = (r.exact === true && skus.length) ? 1 : 2;
      sxFit.set({
        tier: tier,
        source: r.source || 'finder',
        vehicle: r.vehicle || '',
        plate: r.plate || '',
        code: r.code || '',
        skus: skus
      });
      return tier;
    },

    clear: function () { clear(); document.dispatchEvent(new CustomEvent('sx:fit-changed')); },

    get: read,

    /**
     * What tier applies to this particular SKU right now.
     * Tier 1 is deliberately narrow: the stored match has to name this SKU.
     */
    tierFor: function (sku) {
      var m = read();
      if (!m) return 2;
      if (m.tier === 1 && sku && m.skus.length && m.skus.indexOf(sku) !== -1) return 1;
      return 2;
    },

    /** e.g. "2014 Toyota Corolla · plate ABC123" */
    describe: function () {
      var m = read();
      if (!m) return '';
      var bits = [];
      if (m.vehicle) bits.push(m.vehicle);
      if (m.plate) bits.push('plate ' + m.plate.toUpperCase());
      else if (m.code) bits.push('part ' + m.code);
      return bits.join(' · ');
    }
  };

  window.sxFit = sxFit;

  // ------------------------------------------------------ render the badge
  function label(tier) {
    // "Not guaranteed" read as "this might not fit your car". It is narrower
    // than that: length and end fittings DO match, only the force rating is
    // unconfirmed - which is exactly what body() below explains. The chip now
    // says the specific thing rather than delivering a blanket verdict.
    //
    // This is presentation only. The order line still records
    // 'Partial match - force rating not confirmed' either way (see decorate()),
    // and the acknowledgement lives in the cart, so nothing about the record
    // or the protection depends on this wording.
    return tier === 1 ? '100% match' : '';
  }

  /* WHAT THE CUSTOMER IS BEING PROMISED, in the words they will be held to.
     Dave, 14/09/2026: "explain what the gauranteed fit assurance is to the
     customer if they get that too". Three things have to be in here or the
     badge is decoration: what was matched, what counts as not fitting, and
     what we do about it. This sits ON TOP of the Consumer Guarantees Act,
     which cannot be signed away - the line under the panel says so. */
  function body(tier) {
    if (tier === 1) {
      return 'Fit Assurance: this is an exact match to the strut your vehicle left ' +
        'the factory with - length, end fittings and pressure all confirmed against ' +
        'your registration. If it does not fit - it will not attach, will not hold ' +
        'the boot or bonnet up, or stops the vehicle closing properly - we replace ' +
        'it free. You do not pay return postage.';
    }
    // Deliberately about PRESSURE and nothing else. Trim level and hinge
    // geometry were in here before; they are true but they are not what the
    // customer can act on. The force rating is, and the scanner settles it.
    return 'Length and end fittings match your vehicle. What we cannot confirm ' +
      'from here is the force rating - the same model often left the factory ' +
      'with more than one. Scan the label on your old strut and we will match ' +
      'its exact part number, which is the only way to get a guaranteed fit. ' +
      'Without it, the strut is pressurised to the specification you have ' +
      'given us and is not returnable.';
  }

  function render() {
    var hosts = document.querySelectorAll('[data-sx-fit]');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var sku = host.getAttribute('data-sx-fit') || '';
      var tier = sxFit.tierFor(sku);
      host.setAttribute('data-sx-fit-tier', String(tier));

      /* The panel exists only for a confirmed match. Nothing partial, nothing
         cautionary, nothing at all - Dave's ruling 14/09/2026. */
      host.hidden = (tier !== 1);
      if (tier !== 1) continue;

      var titleEl = host.querySelector('[data-sx-fit-title]');
      if (titleEl) titleEl.textContent = label(tier);

      var bodyEl = host.querySelector('[data-sx-fit-body]');
      if (bodyEl) bodyEl.textContent = body(tier);

      var vehEl = host.querySelector('[data-sx-fit-vehicle]');
      if (vehEl) {
        var desc = tier === 1 ? sxFit.describe() : '';
        if (desc) { vehEl.textContent = 'Matched to ' + desc; vehEl.hidden = false; }
        else { vehEl.textContent = ''; vehEl.hidden = true; }
      }

      var ack = host.querySelector('[data-sx-fit-ack]');
      if (ack) ack.hidden = (tier === 1);
    }
    syncSubmit();
  }

  // Kept only to clear stale blocked state - see the note inside.
  function syncSubmit() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var host = form.querySelector('[data-sx-fit]') ||
                 (form.closest('[data-sx-product]') || document).querySelector('[data-sx-fit]');
      if (!host) continue;
      var tier = Number(host.getAttribute('data-sx-fit-tier') || 2);
      var box = host.querySelector('[data-sx-fit-ack] input[type="checkbox"]');
      var btn = form.querySelector('[type="submit"], [name="add"]');
      if (!btn) continue;
      // The gate moved to the cart on 2026-09-02 (assets/sx-cart-ack.js), so
      // nothing here blocks Add to cart any more. This still runs to clear any
      // blocked state left on a button by a cached copy of the old script.
      btn.removeAttribute('aria-disabled');
      delete btn.dataset.sxFitBlocked;
    }
  }

  // ------------------------------------------- attach the tier to the cart
  // Line item properties without a leading underscore are visible at checkout,
  // on the order, and in the notification emails - which is exactly where the
  // customer and the person picking the order both need to see this.
  function decorate(form) {
    var host = form.querySelector('[data-sx-fit]') ||
               (form.closest('[data-sx-product]') || document).querySelector('[data-sx-fit]');
    if (!host) return;
    var sku = host.getAttribute('data-sx-fit') || '';
    var tier = sxFit.tierFor(sku);
    var m = read();

    function put(name, value) {
      if (!value) return;
      var existing = form.querySelector('input[name="properties[' + name + ']"]');
      if (existing) { existing.value = value; return; }
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      input.value = value;
      form.appendChild(input);
    }

    /* WHICH PROPERTIES THE CUSTOMER EVER SEES - fixed at the source 14/09/2026.

       Dave found "Fit Assurance: Partial match - force rating not confirmed"
       printed on the TAX INVOICE for #1006, after it had already been taken off
       the product page, the cart and the order confirmation email. Suppressing
       it one template at a time - the email, then Order Printer Pro, then the
       order status page, then whatever prints next - is a losing game: every
       surface that renders line item properties picks it up by default.

       So the visible property is written ONLY on a confirmed match, where it is
       a promise worth printing. A partial match records the same sentence under
       an underscore name, which Shopify and the printing apps both treat as
       internal: it stays on the order in admin for our records and appears on
       nothing the customer receives. */
    /* NOTHING IS WRITTEN WHEN NOTHING WAS MATCHED. This used to stamp
       _fit_note 'Partial match - force rating not confirmed' and _fit_tier '2'
       onto EVERY add-to-cart, including a plain product page where the
       customer never looked anything up. "Partial match" on a strut that was
       never matched against anything is false, and it destroyed the one
       signal worth having: Dave needs to know who bought through the lookup,
       so he knows whose strut he has to replace if the fit is wrong. When
       every line says it, no line means anything.

       Present now means the customer used the rego lookup, the finder or the
       scanner. Absent means they picked it themselves. */
    if (!m) { return; }

    if (tier === 1) {
      put('Fit Assurance', '100% match - guaranteed fitment');
    } else {
      put('_fit_note', 'Partial match - force rating not confirmed');
    }

    /* THE VEHICLE NAME IS ONLY TRUE OF A PRODUCT THE MATCH NAMES. Without
       this test it was written from whatever was matched most recently, so a
       Jeep scan followed by adding an Alfa Romeo strut off its own product
       page produced a line reading 'Vehicle: Jeep Cherokee Grand Wj
       1999-2005' - on the order and on the PACKING SLIP. Found by the Shopify
       Cowork, 16/09/2026.

       The guarantee was already gated on the SKU by tierFor(); the vehicle
       name was gated on nothing. Same test now applies to both. A finder
       match that names no SKUs records no vehicle, which is the safe
       direction: a missing vehicle costs a line of admin detail, a wrong one
       costs a return. */
    if (m.vehicle && sku && m.skus && m.skus.length && m.skus.indexOf(sku) !== -1) {
      put('Vehicle', m.vehicle);
    }
    put('_fit_tier', String(tier));
    put('_fit_source', m.source);
    if (m.plate) put('_fit_plate', m.plate.toUpperCase());
    if (m.code) put('_fit_code', m.code);
  }

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    var host = form.querySelector('[data-sx-fit]') ||
               (form.closest('[data-sx-product]') || document).querySelector('[data-sx-fit]');
    // No gate here any more. The single acknowledgement - terms, refund policy
    // and measured length - lives in the cart, because that is the last step
    // the theme controls on a non-Plus plan. decorate() still runs on every
    // add, so each line keeps the record of what the customer was shown.
    decorate(form);
    if (window.sxTrack) {
      window.sxTrack('fit_assurance_accepted', {
        fit_tier: host && host.getAttribute('data-sx-fit-tier') === '1' ? 'guaranteed' : 'partial'
      });
    }
  }, true);

  document.addEventListener('change', function (ev) {
    if (ev.target && ev.target.closest && ev.target.closest('[data-sx-fit-ack]')) {
      var h = ev.target.closest('[data-sx-fit]');
      if (h) h.removeAttribute('data-sx-fit-nag');
      syncSubmit();
    }
  });

  document.addEventListener('sx:fit-changed', render);
  document.addEventListener('DOMContentLoaded', render);
  if (document.readyState !== 'loading') render();
}());
